"use client";

import React, { useState } from "react";
import { CheckCircle2, ChevronDown } from "lucide-react";
import { HeroTrialCta } from "@/components/landing/hero-trial-cta";
import { LANDING_FAQS } from "@/lib/landing-faq";
import { useReveal } from "@/lib/use-reveal";

const guarantees = [
  "5-day free trial on all monthly plans",
  "Transparent monthly and annual plans",
  "Cancel anytime",
  "Free calculators, no account required",
];

const faqs = LANDING_FAQS;

function FAQItem({
  faq,
  isOpen,
  onToggle,
}: {
  faq: (typeof faqs)[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-border bg-card rounded-xl overflow-hidden surface-toplight">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${faq.question.slice(0, 20).replace(/\W/g, '')}`}
        className="flex items-center justify-between w-full px-5 py-4 text-left"
      >
        <span className="font-medium text-foreground pr-4">{faq.question}</span>
        <div
          className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        >
          <ChevronDown className="size-5 text-muted-foreground" />
        </div>
      </button>
      <div className="accordion-reveal" data-open={isOpen}>
        <div>
          <section
            id={`faq-panel-${faq.question.slice(0, 20).replace(/\W/g, '')}`}
            className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed"
          >
            {faq.answer}
          </section>
        </div>
      </div>
    </div>
  );
}

export function FinalCTASection() {
  const [sectionRef, isInView] = useReveal<HTMLElement>();
  const [openFAQs, setOpenFAQs] = useState<Set<number>>(new Set([0, 1]));

  return (
    <section ref={sectionRef} className="py-20 bg-card/30 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-16 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-6">Common questions, answered.</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={faq.question} faq={faq} isOpen={openFAQs.has(i)} onToggle={() => setOpenFAQs((prev) => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                return next;
              })} />
            ))}
          </div>
        </div>
        <div data-revealed={isInView} className="reveal reveal-fast text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-[-0.035em] text-balance mb-6">
            Ready to stop{" "}
            <span className="text-primary">
              guessing?
            </span>
          </h2>

          <div className="mx-auto mb-8 max-w-xl [&>div>div]:justify-center">
            <HeroTrialCta placement="final" />
          </div>

          <p className="text-sm text-muted-foreground text-center -mt-4 mb-4">
            Card required, $0 charged today · Cancel anytime · New subscribers only
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {guarantees.map((guarantee, i) => (
              <div
                key={guarantee}
                data-revealed={isInView}
                style={{ transitionDelay: `${160 + i * 80}ms` }}
                className="reveal reveal-fast flex items-center gap-2 text-sm text-muted-foreground"
              >
                <CheckCircle2 className="size-4 text-success" />
                <span>{guarantee}</span>
              </div>
            ))}
          </div>

        </div>

      </div>
    </section>
  );
}
